import React, { useEffect, useState } from "react";

import { Button, Col, Form, Input, Row } from "antd";
import moment from "moment";
import { CardContent, CardHeader, UserAccountDetailsCard } from "./elements";

const formatBirthdateForDisplay = (birthdate) => {
	if (!birthdate) return "";
	const str = String(birthdate).trim();
	const formats = [
		["YYYY-MM-DD", /^\d{4}-\d{2}-\d{2}/],
		["DD-MM-YYYY", /^\d{1,2}-\d{1,2}-\d{4}/],
		["DD/MM/YYYY", /^\d{1,2}\/\d{1,2}\/\d{4}/],
		["MM/DD/YYYY", /^\d{1,2}\/\d{1,2}\/\d{4}/],
	];
	for (const [format, pattern] of formats) {
		if (pattern.test(str)) {
			const parsed = moment(str, format, true);
			if (parsed.isValid()) return parsed.format("MM/DD/YYYY");
		}
	}
	const parsed = moment(str);
	return parsed.isValid() ? parsed.format("MM/DD/YYYY") : "";
};

const AccountDetailsForm = ({ userInfo, translate, onSave, updateProfile }) => {
	const [form] = Form.useForm();
	const hasNationalId = window.PROFILE_HAS_NATIONAL_ID;
	const hasLessBorderRadius = window.HAS_LESS_BORDER_RADIUS;
	const [isNationalIdEmpty, setIsNationalIdEmpty] = useState(false);
	const [isEmailChanged, setIsEmailChanged] = useState(false);

	const normalizeEmail = (value) => (value ?? "").toString().trim();

	const getFormFields = () => {
		const formattedBirthday = formatBirthdateForDisplay(userInfo?.birthdate);

		const fields = {
			name: userInfo?.given_name,
			surname: userInfo?.family_name,
			email: userInfo?.email,
			birthday: formattedBirthday,
			phone: userInfo?.phone_number,
			address: userInfo?.address,
			accepts_sms: userInfo?.accepts_sms,
			accepts_emails: userInfo?.accepts_email,
		};

		if (hasNationalId) {
			fields.nationalId = userInfo?.national_id;
		}

		return fields;
	};

	useEffect(() => {
		form.setFieldsValue(getFormFields());

		// Check if national ID is empty
		if (hasNationalId) {
			setIsNationalIdEmpty(!userInfo?.national_id);
		}

		setIsEmailChanged(false);
	}, [userInfo, form]);

	useEffect(() => {
		form.setFieldsValue(getFormFields());
	}, [updateProfile]);

	const handleSave = () => {
		form.validateFields().then((values) => {
			if (onSave) {
				const attributes = [];

				if (isEmailChanged) {
					attributes.push({
						Name: "email",
						Value: normalizeEmail(values.email),
					});
				}

				if (hasNationalId && values.nationalId) {
					attributes.push({
						Name: "custom:nationalId",
						Value: values.nationalId,
					});
				}

				if (updateProfile) {
					if (values.phone) {
						attributes.push({
							Name: "phone_number",
							Value: values.phone,
						});
					}
					if (values.address) {
						attributes.push({
							Name: "address",
							Value: values.address,
						});
					}
				}

				onSave({
					UserAttributes: attributes,
				});
			}
		});
	};

	return (
		<UserAccountDetailsCard>
			<CardHeader>{translate("Account Details")}</CardHeader>
			<CardContent hasLessBorderRadius={hasLessBorderRadius}>
				<Form
					form={form}
					initialValues={getFormFields()}
					onValuesChange={(_, allValues) => {
						setIsEmailChanged(
							normalizeEmail(allValues?.email) !== normalizeEmail(userInfo?.email)
						);
					}}>
					<Row
						xs={24}
						gutter={10}>
						<Col xs={12}>
							<Form.Item
								label={translate("Name")}
								name="name">
								<Input disabled />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								label={translate("Surname")}
								name="surname">
								<Input disabled />
							</Form.Item>
						</Col>
					</Row>
					<Row
						xs={24}
						gutter={hasNationalId ? 10 : 0}>
						<Col xs={hasNationalId ? 12 : 24}>
							<Form.Item
								label={translate("Email")}
								name="email"
								rules={[
									{
										required: true,
										message: translate("PLEASE_INPUT_EMAIL"),
									},
									{
										type: "email",
										message: translate("EMAIL_FORMAT_INVALID"),
									},
								]}>
								<Input type="email" />
							</Form.Item>
						</Col>
						{hasNationalId && (
							<Col xs={12}>
								<Form.Item
									label={translate("National ID")}
									name="nationalId"
									rules={
										isNationalIdEmpty
											? [
													{
														required: true,
														message: translate(
															"Please enter your National ID"
														),
													},
												]
											: []
									}>
									<Input disabled={!isNationalIdEmpty} />
								</Form.Item>
							</Col>
						)}
					</Row>
					<Row
						xs={24}
						gutter={10}>
						<Col xs={12}>
							<Form.Item
								label={translate("Birthday")}
								name="birthday">
								<Input disabled />
							</Form.Item>
						</Col>
						<Col xs={12}>
							<Form.Item
								label={translate("Phone")}
								name="phone"
								rules={
									updateProfile
										? [
												{
													required: true,
													message: translate(
														"Please enter your phone number"
													),
												},
											]
										: []
								}>
								<Input
									key={`phone-${updateProfile}`}
									disabled={!updateProfile}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Form.Item
						label={translate("Address")}
						name="address"
						rules={
							updateProfile
								? [
										{
											required: true,
											message: translate("Please enter your address"),
										},
									]
								: []
						}>
						<Input.TextArea
							key={`address-${updateProfile}`}
							disabled={!updateProfile}
						/>
					</Form.Item>

					{((hasNationalId && isNationalIdEmpty) || updateProfile || isEmailChanged) && (
						<Form.Item>
							<Button
								className="change-password-button"
								type="primary"
								onClick={handleSave}>
								{translate("Save")}
							</Button>
						</Form.Item>
					)}
				</Form>
			</CardContent>
		</UserAccountDetailsCard>
	);
};

export default AccountDetailsForm;
